import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  type OnChanges,
  type OnDestroy,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type {
  EditInventoryManufacturer,
  InventoryManufacturer,
} from '../../../shared/models/api/inventory-manufacturer.model';
import type { ControlsOf } from '../../../shared/models/controls-of.model';
import type { NgChanges } from '../../../shared/models/ng-changes.model';
import { InventoryManufacturerService } from '../../../shared/services/api/inventory-manufacturer.service';
import { FormIdService } from '../../../shared/services/form-id.service';
import { OpaValidators } from '../../../shared/validators/opa.validators';
import type { EditInventoryManufacturerFormValue } from './inventory-manufacturer-edit.models';

@Component({
  selector: 'opa-inventory-manufacturer-edit',
  imports: [CommonModule, PorscheDesignSystemModule, ReactiveFormsModule, LetDirective],
  templateUrl: './inventory-manufacturer-edit.component.html',
  styleUrl: './inventory-manufacturer-edit.component.scss',
})
export class InventoryManufacturerEditComponent implements OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly inventoryManufacturerService = inject(InventoryManufacturerService);
  private readonly toastManager = inject(ToastManager);
  readonly formId = inject(FormIdService).getId();

  @Input({ required: true }) open = false;
  @Input({ required: true }) inventoryManufacturer: InventoryManufacturer | undefined = undefined;
  @Output() dismiss = new EventEmitter<boolean | undefined>();

  private readonly subSink = new SubSink();
  readonly form = this.buildForm();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());

  ngOnChanges(changes: NgChanges<InventoryManufacturerEditComponent>): void {
    if (changes.open && this.open) this.resetForm(this.inventoryManufacturer);
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(success?: boolean): void {
    this.dismiss.emit(success);
  }

  submit(): void {
    if (!this.form.valid || !this.inventoryManufacturer) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.next(true);
    const formValue = this.form.getRawValue();
    const manufacturer = this.formValueToManufacturer(formValue);
    this.subSink.sink = this.inventoryManufacturerService
      .editInventoryManufacturer(this.inventoryManufacturer.id, manufacturer)
      .pipe(
        tap({
          next: (editedManufacturer) => {
            this.toastManager.addMessage({
              text: `Manufacturer '${editedManufacturer.description}' edited`,
              state: 'success',
            });
            this.submitting.next(false);
            this.close(true);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<EditInventoryManufacturerFormValue>> {
    const form = this.formBuilder.group<ControlsOf<EditInventoryManufacturerFormValue>>({
      description: this.formBuilder.control(null, [OpaValidators.required()]),
    });

    return form;
  }

  private formValueToManufacturer(
    form: EditInventoryManufacturerFormValue,
  ): EditInventoryManufacturer {
    const inventoryManufacturer: EditInventoryManufacturer = {
      description: form.description!,
      name: this.inventoryManufacturer!.name,
    };
    return inventoryManufacturer;
  }

  private resetForm(inventoryManufacturer?: InventoryManufacturer): void {
    this.form.reset();
    if (!inventoryManufacturer) return;

    this.form.patchValue({
      description: inventoryManufacturer.description,
    });
  }
}
